import React, { createContext, useReducer } from "react";
import type { ProductCode, ProductConfig, EditorLocaleData } from "../types";
// @ts-expect-error - reducer.js has no type declaration
import reducer from "./reducer";

type EditorState = ReturnType<typeof getInitialState>;
type EditorAction = { type: string; payload?: unknown };
type EditorContextValue = [EditorState, React.Dispatch<EditorAction>];

export const EditorContext = createContext<EditorContextValue | undefined>(undefined);

// Default configuration for most products
const defaultProductConfig: ProductConfig = {
  readOnlyLocales: ["en-default"],
  currentLocale: "en",
  localeKeys: ["default", "en"],
  isSettingsLocale: false,
};

// Product-specific configuration
const productConfig = {
  // server-managed translation domains
  fi: {
    ...defaultProductConfig,
    // The `fi` translation domain cuts across all products, so we
    // initialize state differently on the "institution settings and products" page.
    // The `en` locale is generated by the back end from institution settings.
    // It is the source of truth for current values in settings, and is not editable.
    readOnlyLocales: ["en"],
    currentLocale: null,
    localeKeys: ["en"],
    isSettingsLocale: true,
  },
  server: {
    ...defaultProductConfig,
    // User facing messages defined on the server may not be customized
    // in English. Only non-English locales may be edited for server messages.
    readOnlyLocales: ["en-default", "en"],
    currentLocale: "default",
  },

  // Client-managed, product-specific domains
  bao: defaultProductConfig,
  cao: defaultProductConfig,
  olb: defaultProductConfig,
  app: defaultProductConfig,
  sao: defaultProductConfig,
  lending: defaultProductConfig,
} as const;

const getInitialState = (product: ProductCode) => {
  const config = productConfig[product];
  const locales = Object.fromEntries(
    config.localeKeys.map((key) => [
      key,
      {
        isEnabled: true,
        isPosting: false,
        hasPostingFailed: false,
        messages: {},
        visibleMessages: {},
        raw: "",
      } as EditorLocaleData,
    ])
  );

  return {
    product,
    isAddingLocale: false,
    isLoadingLocales: false,
    hasLoadingFailed: false,
    hasLocaleAddFailed: false,
    currentLocale: config.currentLocale,
    readOnlyLocales: config.readOnlyLocales,
    locales,
  } as const;
};

// Exported for testing purposes
export const initialState = getInitialState("bao");

interface EditorContextProviderProps {
  product: ProductCode;
  children: React.ReactNode;
  /** For use in unit test where it's useful to manually set the provider state */
  _testStateOverrides?: Record<string, unknown>;
}

const EditorContextProvider = ({
  product,
  children,
  _testStateOverrides,
}: EditorContextProviderProps) => {
  const defaultState = getInitialState(product);
  const initialProviderState = { ...defaultState, ..._testStateOverrides } as typeof defaultState;
  const [state, dispatch] = useReducer(reducer, initialProviderState);

  return <EditorContext.Provider value={[state, dispatch]}>{children}</EditorContext.Provider>;
};

export default EditorContextProvider;
